import { useState, useRef, useEffect } from "react";
import { Command, Sparkles, Send, Loader2, Paperclip } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { VoiceInputButton } from "./VoiceInputButton";

interface AIInputBarProps {
  organizationId: string;
  onCommandExecuted?: (result: any) => void;
  placeholder?: string;
  className?: string;
}

export function AIInputBar({ 
  organizationId, 
  onCommandExecuted,
  placeholder = "Ask Ritual anything...",
  className 
}: AIInputBarProps) {
  const [query, setQuery] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isVoiceListening, setIsVoiceListening] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // Keyboard shortcut: Cmd/Ctrl + K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    
    if (!query.trim() || isProcessing) return;
    
    setIsProcessing(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast({
          variant: "destructive",
          title: "Not authenticated",
          description: "Please sign in to use AI commands",
        });
        return;
      }

      // Call the ritual-agent-api with a natural language query
      const response = await supabase.functions.invoke("ritual-agent-api", {
        body: {
          function: "ritual.ai.process_natural_language",
          params: {
            query: query.trim(),
            context: { organizationId }
          }
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (response.error) {
        throw new Error(response.error.message || "Failed to process command");
      }

      const result = response.data;
      
      if (result.success) {
        toast({
          title: "Command executed",
          description: result.message || "Your request has been processed",
        });
        setQuery("");
        onCommandExecuted?.(result);
      } else {
        toast({
          variant: "destructive",
          title: "Command failed",
          description: result.error || "Failed to process your request",
        });
      }
    } catch (error: any) {
      console.error("AI command error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Something went wrong",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form 
      onSubmit={handleSubmit}
      className={cn(
        "relative group",
        className
      )}
    >
      <div 
        className={cn(
          "relative flex items-center gap-3 rounded-xl border bg-card px-4 py-3 transition-all duration-200",
          isFocused 
            ? "border-primary/50 shadow-lg shadow-primary/10" 
            : "border-border hover:border-border/80",
          isProcessing && "opacity-80"
        )}
      >
        {/* Voice Input */}
        <VoiceInputButton
          onTranscript={(text) => {
            setQuery(prev => prev + (prev ? ' ' : '') + text);
            inputRef.current?.focus();
          }}
          onListeningChange={setIsVoiceListening}
          disabled={isProcessing}
          size="sm"
          className={isVoiceListening ? 'ring-2 ring-destructive/50' : ''}
        />

        {/* Icon */}
        <div className={cn(
          "flex items-center justify-center rounded-lg p-1.5 transition-colors",
          isFocused || isVoiceListening ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
        )}>
          {isProcessing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
        </div>

        {/* Input */}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          disabled={isProcessing}
          className={cn(
            "flex-1 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
            "text-foreground"
          )}
        />

        {/* Keyboard shortcut hint or submit button */}
        <div className="flex items-center gap-2">
          {!query && !isFocused && (
            <div className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground">
              <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                <Command className="h-3 w-3" /><span>K</span>
              </kbd>
            </div>
          )}
          
          {query && (
            <button
              type="submit"
              disabled={isProcessing || !query.trim()}
              className={cn(
                "flex items-center justify-center rounded-lg p-1.5 transition-all",
                "bg-primary text-primary-foreground hover:bg-primary/90",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              <Send className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Suggested actions - shown on focus */}
      {isFocused && !query && (
        <div className="absolute top-full left-0 right-0 mt-2 p-2 rounded-lg border bg-popover shadow-lg z-10">
          <p className="text-xs text-muted-foreground mb-2 px-2">Try saying:</p>
          <div className="space-y-1">
            {[
              "Start employee onboarding for Sarah",
              "What's overdue?",
              "Create an SOP for client offboarding",
              "Show me today's tasks",
            ].map((suggestion, i) => (
              <button
                key={i}
                type="button"
                onClick={() => {
                  setQuery(suggestion);
                  inputRef.current?.focus();
                }}
                className="w-full text-left text-sm px-3 py-2 rounded-lg hover:bg-muted transition-colors touch-target"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}
    </form>
  );
}
